/* eslint-env browser */

export const CONSENT_STORAGE_KEY = "sixfab.consent.v1";
export const CONSENT_VERSION = 2;
export const CONSENT_MAX_AGE = 180 * 24 * 60 * 60 * 1000;
const listeners = new Set();
let accountId = null;
let persistence;

function emptyConsent() {
  return { analytics: false, sessionReplay: false, decided: false };
}

function normalizeRecord(value) {
  const hasExperience = value && Object.prototype.hasOwnProperty.call(value, "experience");
  const experience = value && (hasExperience ? value.experience : value.sessionReplay);
  if (
    !value || ![1, CONSENT_VERSION].includes(value.version)
    || typeof value.analytics !== "boolean" || typeof experience !== "boolean"
    || !Number.isFinite(value.updatedAt) || Date.now() - value.updatedAt >= CONSENT_MAX_AGE
    // Allow a small difference between the browser and API server clocks.
    || value.updatedAt > Date.now() + 5 * 60 * 1000
    || (value.accountId != null && (typeof value.accountId !== "string" || !value.accountId))
  ) return null;
  return {
    version: value.version,
    analytics: value.analytics,
    experience,
    updatedAt: value.updatedAt,
    ...(value.accountId ? { accountId: value.accountId } : {}),
  };
}

function writeRecord(record) {
  try {
    if (record) localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    else localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch (error) {
    // The current page still respects the choice when browser storage is blocked.
  }
}

function readRecord() {
  try {
    const value = JSON.parse(localStorage.getItem(CONSENT_STORAGE_KEY));
    const record = normalizeRecord(value);
    if (record && !Object.prototype.hasOwnProperty.call(value, "experience")) writeRecord(record);
    return record;
  } catch (error) {
    return null;
  }
}

function recordConsent(record) {
  // An account cache cannot authorize tracking before that user is authenticated.
  if (!record || (record.accountId || null) !== accountId) return emptyConsent();
  return { analytics: record.analytics, sessionReplay: record.experience, decided: true };
}

let storedRecord = readRecord();
let consent = recordConsent(storedRecord);

function queueGoogleConsent() {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

function googleConsent(mode) {
  queueGoogleConsent("consent", mode, {
    analytics_storage: consent.analytics ? "granted" : "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
}

googleConsent("default");

export function getConsent() {
  return { ...consent };
}

export function hasAnalyticsConsent() {
  return consent.analytics;
}

export function subscribeConsent(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function notify(previous, metadata = {}) {
  googleConsent("update");
  listeners.forEach((callback) => {
    try {
      callback(getConsent(), previous, metadata);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("Consent update failed:", error);
    }
  });
}

export function setConsentAccount(id) {
  accountId = id || null;
}

export function setConsentPersistence(handler) {
  persistence = handler;
}

export function applyConsentRecord(record, metadata = {}) {
  const previous = metadata.previous || getConsent();
  storedRecord = normalizeRecord(record);
  consent = recordConsent(storedRecord);
  writeRecord(storedRecord);
  notify(previous, metadata);
  return getConsent();
}

export function fromAccountConsent(id, value) {
  if (!value || value.version !== CONSENT_VERSION || !Number.isFinite(value.updated_at)) {
    return null;
  }
  return normalizeRecord({
    version: value.version,
    analytics: value.analytics,
    experience: value.experience,
    updatedAt: value.updated_at * 1000,
    accountId: String(id),
  });
}

export function commitConsent(preferences) {
  return applyConsentRecord({
    version: CONSENT_VERSION,
    analytics: preferences.analytics === true,
    experience: preferences.sessionReplay === true,
    updatedAt: Date.now(),
    ...(accountId ? { accountId } : {}),
  }, { source: "save" });
}

export function saveConsent(preferences) {
  if (persistence) return persistence(preferences);
  return commitConsent(preferences);
}

export function restrictConsent(preferences) {
  const previous = getConsent();
  consent = {
    ...consent,
    analytics: consent.analytics && preferences.analytics === true,
    sessionReplay: consent.sessionReplay && preferences.sessionReplay === true,
  };
  if (
    previous.analytics !== consent.analytics || previous.sessionReplay !== consent.sessionReplay
  ) {
    notify(previous, { source: "pending" });
  }
  return previous;
}

export function openConsentPreferences() {
  window.dispatchEvent(new CustomEvent("sixfab:open-consent"));
}

export function requiresConsentReload(next, previous) {
  return previous.analytics && !next.analytics;
}

window.addEventListener("storage", (event) => {
  if (event.key !== CONSENT_STORAGE_KEY && event.key !== null) return;
  const record = readRecord();
  if (record && (record.accountId || null) !== accountId) return;
  const previous = getConsent();
  storedRecord = record;
  consent = recordConsent(record);
  notify(previous, { source: "storage" });
});
